/**
 * IRS Publication 1220 fixed-width layouts.
 *
 * Sources (positions are core-api's, which match the current Pub 1220 with 18 amount codes):
 *  - `records/efile.Brecord.ts`  -> B_RECORD_COMMON (payee record, positions 2 to 543)
 *  - `records/efile.Arecord.ts`  -> A_RECORD (issuer record; core-api builds it by string
 *    concatenation rather than an array, so the array here was derived field by field from
 *    that writer, in its order, with its lengths)
 *  - `records/efile.Trecord.ts`  -> T_RECORD (transmitter record, same derivation)
 *  - `records/efile.Crecord.ts`  -> C_RECORD (end of issuer)
 *  - `records/efile.Frecord.ts`  -> F_RECORD (end of transmission)
 *  - `efile/{form}/{form}-544-7xx-Struc*.ts` -> FORM_BLOCK_544_750 (the form-specific tail)
 *
 * Every record is 750 characters. A field's `key` is empty when the span is filler.
 */

export interface Pub1220Field {
  /** Canonical Pub 1220 field name, or "" for filler. */
  key: string;
  length: number;
  padType: "start" | "end";
  padChar: string;
}

/** Fixed record length for every Pub 1220 record type. */
export const PUB1220_RECORD_LENGTH = 750;

/**
 * T record: the transmitter. Derived from generateTRecordString in efile.Trecord.ts, in the
 * order that function concatenates, so PaymentYear is at 2 to 5 and TestFileIndicator at 28.
 */
export const T_RECORD: Pub1220Field[] = [
  { key: "RecordType", length: 1, padType: "end", padChar: " " },
  { key: "PaymentYear", length: 4, padType: "end", padChar: " " },
  { key: "PriorYearDataIndicator", length: 1, padType: "end", padChar: " " },
  { key: "TransmitterTIN", length: 9, padType: "end", padChar: " " },
  { key: "TransmitterControlCode", length: 5, padType: "end", padChar: " " },
  { key: "", length: 7, padType: "end", padChar: " " },
  { key: "TestFileIndicator", length: 1, padType: "end", padChar: " " },
  { key: "ForeignEntityIndicator", length: 1, padType: "end", padChar: " " },
  { key: "TransmitterName", length: 40, padType: "end", padChar: " " },
  { key: "TransmitterNameContinuation", length: 40, padType: "end", padChar: " " },
  { key: "CompanyName", length: 40, padType: "end", padChar: " " },
  { key: "CompanyNameContinuation", length: 40, padType: "end", padChar: " " },
  { key: "CompanyMailingAddress", length: 40, padType: "end", padChar: " " },
  { key: "CompanyCity", length: 40, padType: "end", padChar: " " },
  { key: "CompanyState", length: 2, padType: "end", padChar: " " },
  { key: "CompanyZIPCode", length: 9, padType: "end", padChar: " " },
  { key: "", length: 15, padType: "end", padChar: " " },
  { key: "TotalNumberOfPayees", length: 8, padType: "start", padChar: "0" },
  { key: "ContactName", length: 40, padType: "end", padChar: " " },
  { key: "ContactTelephoneNumber", length: 15, padType: "end", padChar: " " },
  { key: "ContactEmailAddress", length: 50, padType: "end", padChar: " " },
  { key: "", length: 91, padType: "end", padChar: " " },
  { key: "RecordSequenceNumber", length: 8, padType: "start", padChar: "0" },
  { key: "", length: 10, padType: "end", padChar: " " },
  { key: "VendorIndicator", length: 1, padType: "end", padChar: " " },
  { key: "VendorName", length: 40, padType: "end", padChar: " " },
  { key: "VendorMailingAddress", length: 40, padType: "end", padChar: " " },
  { key: "VendorCity", length: 40, padType: "end", padChar: " " },
  { key: "VendorState", length: 2, padType: "end", padChar: " " },
  { key: "VendorZIPCode", length: 9, padType: "end", padChar: " " },
  { key: "VendorContactName", length: 40, padType: "end", padChar: " " },
  { key: "VendorContactTelephoneNumber", length: 15, padType: "end", padChar: " " },
  { key: "", length: 35, padType: "end", padChar: " " },
  { key: "VendorForeignEntityIndicator", length: 1, padType: "end", padChar: " " },
  { key: "", length: 8, padType: "end", padChar: " " },
];

/**
 * A record: the issuer. Derived from formatARecord in efile.Arecord.ts. Note that Type of
 * Return sits at 26 to 27 and Amount Codes at 28 to 45 (eighteen codes), which is the current
 * Pub 1220 layout; a sixteen-code reading is the pre-2018 one and is off by a column.
 */
export const A_RECORD: Pub1220Field[] = [
  { key: "RecordType", length: 1, padType: "end", padChar: " " },
  { key: "PaymentYear", length: 4, padType: "end", padChar: " " },
  { key: "CombinedFederalStateFiling", length: 1, padType: "end", padChar: " " },
  { key: "", length: 5, padType: "end", padChar: " " },
  { key: "IssuerTIN", length: 9, padType: "start", padChar: " " },
  { key: "IssuerNameControl", length: 4, padType: "end", padChar: " " },
  { key: "LastFilingIndicator", length: 1, padType: "end", padChar: " " },
  { key: "TypeOfReturn", length: 2, padType: "end", padChar: " " },
  { key: "AmountCodes", length: 18, padType: "end", padChar: " " },
  { key: "", length: 6, padType: "end", padChar: " " },
  { key: "ForeignEntityIndicator", length: 1, padType: "end", padChar: " " },
  { key: "FirstIssuerNameLine", length: 40, padType: "end", padChar: " " },
  { key: "SecondIssuerNameLine", length: 40, padType: "end", padChar: " " },
  { key: "TransferAgentIndicator", length: 1, padType: "end", padChar: " " },
  { key: "IssuerShippingAddress", length: 40, padType: "end", padChar: " " },
  { key: "IssuerCity", length: 40, padType: "end", padChar: " " },
  { key: "IssuerState", length: 2, padType: "end", padChar: " " },
  { key: "IssuerZIPCode", length: 9, padType: "end", padChar: " " },
  { key: "IssuerTelephone", length: 15, padType: "end", padChar: " " },
  { key: "", length: 260, padType: "end", padChar: " " },
  { key: "RecordSequenceNumber", length: 8, padType: "start", padChar: "0" },
  { key: "", length: 243, padType: "end", padChar: " " },
];

/** C record: end of issuer. Derived from createCRecord in efile.Crecord.ts. */
export const C_RECORD: Pub1220Field[] = [
  { key: "RecordType", length: 1, padType: "end", padChar: " " },
  { key: "NumberOfPayees", length: 8, padType: "start", padChar: "0" },
  { key: "", length: 6, padType: "end", padChar: " " },
  { key: "ControlTotal1", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal2", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal3", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal4", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal5", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal6", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal7", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal8", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal9", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal10", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal11", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal12", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal13", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal14", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal15", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal16", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal17", length: 18, padType: "start", padChar: "0" },
  { key: "ControlTotal18", length: 18, padType: "start", padChar: "0" },
  { key: "", length: 160, padType: "end", padChar: " " },
  { key: "RecordSequenceNumber", length: 8, padType: "start", padChar: "0" },
  { key: "", length: 243, padType: "end", padChar: " " },
];

/** F record: end of transmission. Derived from createFRecord in efile.Frecord.ts. */
export const F_RECORD: Pub1220Field[] = [
  { key: "RecordType", length: 1, padType: "end", padChar: " " },
  { key: "NumberOfARecords", length: 8, padType: "start", padChar: "0" },
  { key: "Zeros", length: 21, padType: "start", padChar: "0" },
  { key: "", length: 19, padType: "end", padChar: " " },
  { key: "TotalNumberOfPayees", length: 8, padType: "start", padChar: "0" },
  { key: "", length: 442, padType: "end", padChar: " " },
  { key: "RecordSequenceNumber", length: 8, padType: "start", padChar: "0" },
  { key: "", length: 243, padType: "end", padChar: " " },
];

/**
 * B record, positions 2 to 543: copied verbatim from bRecordStructure1_543 in
 * efile.Brecord.ts. Eighteen payment amounts of twelve digits each, in cents.
 */
export const B_RECORD_COMMON: Pub1220Field[] = [
  { key: "RecordType", length: 1, padType: "end", padChar: " " },
    { key: "PaymentYear", length: 4, padType: "end", padChar: " " },
    { key: "CorrectedReturnIndicator", length: 1, padType: "end", padChar: " " },
    { key: "NameControl", length: 4, padType: "end", padChar: " " },
    { key: "TypeOfTIN", length: 1, padType: "end", padChar: " " },
    { key: "PayeesTIN", length: 9, padType: "start", padChar: " " },
    { key: "IssuersAccountNumberForPayee", length: 20, padType: "end", padChar: " " },
    { key: "IssuersOfficeCode", length: 4, padType: "end", padChar: " " },
    { key: "", length: 10, padType: "end", padChar: " " },
    { key: "PaymentAmount1", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount2", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount3", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount4", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount5", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount6", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount7", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount8", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount9", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount10", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount11", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount12", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount13", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount14", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount15", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount16", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount17", length: 12, padType: "start", padChar: "0" },
    { key: "PaymentAmount18", length: 12, padType: "start", padChar: "0" },
    { key: "", length: 16, padType: "end", padChar: " " },
    { key: "ForeignCountryIndicator", length: 1, padType: "end", padChar: " " },
    { key: "FirstPayeeNameLine", length: 40, padType: "end", padChar: " " },
    { key: "SecondPayeeNameLine", length: 40, padType: "end", padChar: " " },
    { key: "PayeeMailingAddress", length: 40, padType: "end", padChar: " " },
    { key: "", length: 40, padType: "end", padChar: " " },
    { key: "PayeeCity", length: 40, padType: "end", padChar: " " },
    { key: "PayeeState", length: 2, padType: "end", padChar: " " },
    { key: "PayeeZIPCode", length: 9, padType: "end", padChar: " " },
    { key: "", length: 1, padType: "end", padChar: " " },
    { key: "RecordSequenceNumber", length: 8, padType: "start", padChar: "0" },
    { key: "", length: 36, padType: "end", padChar: " " },
];

/**
 * B record, positions 544 to 750: the form-specific tail.
 *
 * These are read off Publication 1220 (Rev. 5-2026, Tax Year 2025), Part C. Sec. 3, "Payee
 * \"B\" Record - Record Layout Positions 544-750 for Form X" - the compact position grid at
 * the end of each form's section. Where core-api's own {form}-544-7xx-Struc*.ts disagreed
 * with the publication, the publication wins; the four places that happened are noted below.
 */
export const FORM_BLOCK_544_750: Record<string, Pub1220Field[]> = {
  // 1098-E: 544-546 blank, 547 origination fees indicator, 663-722 special data.
  "1098-E": [
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "OriginationFees_CapitalizedInterestIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 115, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "", length: 26, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1098-T: 544 TIN solicitation, 547-549 the three student indicators.
  "1098-T": [
    { key: "StudentTINSolicitation", length: 1, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "HalfTimeStuIndicator", length: 1, padType: "end", padChar: " " },
    { key: "GraduateStuInd", length: 1, padType: "end", padChar: " " },
    { key: "AcademicPeriodInd", length: 1, padType: "end", padChar: " " },
    { key: "", length: 113, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "", length: 26, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-B: Absent from core-api entirely; read straight off Pub 1220.
  "1099-B": [
    { key: "SecondTINNotice", length: 1, padType: "end", padChar: " " },
    { key: "NoncoveredSecurityIndicator", length: 1, padType: "end", padChar: " " },
    { key: "TypeOfGainOrLossIndicator", length: 1, padType: "end", padChar: " " },
    { key: "GrossProceedsIndicator", length: 1, padType: "end", padChar: " " },
    { key: "DateSoldOrDisposed", length: 8, padType: "end", padChar: " " },
    { key: "CUSIPNumber", length: 13, padType: "end", padChar: " " },
    { key: "DescriptionOfProperty", length: 39, padType: "end", padChar: " " },
    { key: "DateAcquired", length: 8, padType: "end", padChar: " " },
    { key: "LossNotAllowedIndicator", length: 1, padType: "end", padChar: " " },
    { key: "Form8949CheckboxIndicator", length: 1, padType: "end", padChar: " " },
    { key: "CollectablesCheckboxIndicator", length: 1, padType: "end", padChar: " " },
    { key: "FATCAFilingRequirementIndicator", length: 1, padType: "end", padChar: " " },
    { key: "QOFCheckboxIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 42, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "CombinedFederalStateCode", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-C: 544-546 blank, 547 event code, 556-594 debt description (39).
  "1099-C": [
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "IdentifiableEventCode", length: 1, padType: "end", padChar: " " },
    { key: "IdentifiableDate", length: 8, padType: "end", padChar: " " },
    { key: "DebtDescription", length: 39, padType: "end", padChar: " " },
    { key: "PersonalLiabilityIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 67, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "", length: 26, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-DIV: 544 is Second TIN Notice, which core-api read as filler.
  "1099-DIV": [
    { key: "SecondTINNotice", length: 1, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "ForeignCountry", length: 40, padType: "end", padChar: " " },
    { key: "FatcaRequirement", length: 1, padType: "end", padChar: " " },
    { key: "", length: 75, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "CombinedFederalStateCode", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-INT: 544 is Second TIN Notice, which core-api read as filler.
  "1099-INT": [
    { key: "SecondTINNotice", length: 1, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "ForeignCountryorU_S_Possession", length: 40, padType: "end", padChar: " " },
    { key: "CUSIPNumber", length: 13, padType: "end", padChar: " " },
    { key: "FATCAFilingRequirementIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 62, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "CombinedFederalStateCode", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-K: 609-662 is blank and 663-722 is Special Data; core-api ran one 114 filler over both.
  "1099-K": [
    { key: "SecondTINNotice", length: 1, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "FilerTypeIndicator", length: 1, padType: "end", padChar: " " },
    { key: "PaymentTypeIndicator", length: 1, padType: "end", padChar: " " },
    { key: "NumberPaymentTransaction", length: 13, padType: "start", padChar: "0" },
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "PSENameTelephone", length: 40, padType: "end", padChar: " " },
    { key: "MerchantCategoryCode", length: 4, padType: "end", padChar: " " },
    { key: "", length: 54, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "CombinedFederalStateCode", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-LTC: core-api stopped at 748; the record runs to 750.
  "1099-LTC": [
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "PaymentTypeIndicator", length: 1, padType: "end", padChar: " " },
    { key: "InsuredSSN", length: 9, padType: "end", padChar: " " },
    { key: "InsuredName", length: 40, padType: "end", padChar: " " },
    { key: "InsuredAddress", length: 40, padType: "end", padChar: " " },
    { key: "InsuredCity", length: 40, padType: "end", padChar: " " },
    { key: "InsuredState", length: 2, padType: "end", padChar: " " },
    { key: "InsuredZipCode", length: 9, padType: "end", padChar: " " },
    { key: "StatusofIllnessIndicator", length: 1, padType: "end", padChar: " " },
    { key: "DateCertified", length: 8, padType: "end", padChar: " " },
    { key: "QualifiedContractIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 25, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-MISC: 544 Second TIN Notice, 547 direct sales, 548 FATCA.
  "1099-MISC": [
    { key: "SecondTINNotice", length: 1, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "DirectSalesIndicator", length: 1, padType: "end", padChar: " " },
    { key: "FatcaFilingRequirementIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 114, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "CombinedFederalStateCode", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-NEC: No FATCA field on NEC; 548-662 is one blank run.
  "1099-NEC": [
    { key: "SecondTINNotice", length: 1, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "DirectSalesIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 115, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "CombinedFederalStateCode", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-R: Distribution code is two characters at 545-546; Y (QCD) was added for TY2025.
  "1099-R": [
    { key: "", length: 1, padType: "end", padChar: " " },
    { key: "DistributionCode", length: 2, padType: "end", padChar: " " },
    { key: "TaxableAmountNotDeterminedIndicator", length: 1, padType: "end", padChar: " " },
    { key: "IRA_SEP_SIMPLEIndicator", length: 1, padType: "end", padChar: " " },
    { key: "TotalDistributionIndicator", length: 1, padType: "end", padChar: " " },
    { key: "PercentageofTotalDistribution", length: 2, padType: "end", padChar: " " },
    { key: "FirstYearofDesignatedRothContribution", length: 4, padType: "end", padChar: " " },
    { key: "FATCAFilingRequirementIndicator", length: 1, padType: "end", padChar: " " },
    { key: "DateofPayment", length: 8, padType: "end", padChar: " " },
    { key: "", length: 98, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "CombinedFederalStateCode", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-SA: 547 is the Medicare Advantage MSA indicator, not a generic MSA flag.
  "1099-SA": [
    { key: "", length: 1, padType: "end", padChar: " " },
    { key: "DistributionCode", length: 1, padType: "end", padChar: " " },
    { key: "", length: 1, padType: "end", padChar: " " },
    { key: "MedicareAdvantageMSAIndicator", length: 1, padType: "end", padChar: " " },
    { key: "HSAIndicator", length: 1, padType: "end", padChar: " " },
    { key: "ArcherMSAIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 113, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "StateIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "LocalIncomeTaxWithheld", length: 12, padType: "start", padChar: "0" },
    { key: "", length: 2, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 3921: core-api declared Date Option Exercised as 1 character; it is 8 (555-562), so
  // every field after it was misread by seven positions.
  "3921": [
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "DateOptionGranted", length: 8, padType: "end", padChar: " " },
    { key: "DateOptionExercised", length: 8, padType: "end", padChar: " " },
    { key: "NumberofSharesTransferred", length: 8, padType: "start", padChar: "0" },
    { key: "", length: 4, padType: "end", padChar: " " },
    { key: "IfOtherThanTransferorInformation", length: 40, padType: "end", padChar: " " },
    { key: "", length: 48, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "", length: 26, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 3922: Four dates and a share count, then filler.
  "3922": [
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "DateOptionGrantedToTransferrer", length: 8, padType: "end", padChar: " " },
    { key: "DateOptionExercisedByTransferrer", length: 8, padType: "end", padChar: " " },
    { key: "NoOfSharesTransferred", length: 8, padType: "start", padChar: "0" },
    { key: "LegalDateTransferredByTransferror", length: 8, padType: "end", padChar: " " },
    { key: "", length: 84, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "", length: 26, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 1099-Q: not in core-api. Read off Pub 1220 (Rev. 5-2026), "Payee B Record - Record Layout
  // Positions 544-750 for Form 1099-Q". There is no distribution code in a 1220 file.
  "1099-Q": [
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "TrusteeToTrusteeTransferIndicator", length: 1, padType: "end", padChar: " " },
    { key: "TypeOfTuitionPayment", length: 1, padType: "end", padChar: " " },
    { key: "DesignatedBeneficiaryIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 113, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "", length: 26, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
  // 5498-SA: core-api declared only 6 characters; the record runs to 750.
  "5498-SA": [
    { key: "", length: 3, padType: "end", padChar: " " },
    { key: "MedicareAdvantageMSAIndicator", length: 1, padType: "end", padChar: " " },
    { key: "HSAIndicator", length: 1, padType: "end", padChar: " " },
    { key: "ArcherMSAIndicator", length: 1, padType: "end", padChar: " " },
    { key: "", length: 113, padType: "end", padChar: " " },
    { key: "SpecialDataEntries", length: 60, padType: "end", padChar: " " },
    { key: "", length: 26, padType: "end", padChar: " " },
    { key: "", length: 2, padType: "end", padChar: " " },
  ],
};
